import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, IconButton, TextField, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WhatsNewAPIs, { iFeedbackOption, iFeedbackWidgetResponse } from "../../apis/whatsNew.apis";
import BaseAPIs from "../../apis/base.apis";

type Props = {
    open: boolean;
    onClose: () => void;
};

/** Appends an alpha channel to a 6-digit hex colour, leaving other formats untouched. */
const tint = (color: string, alpha: string) => (/^#[0-9a-f]{6}$/i.test(color) ? color + alpha : color);

export default function FeedbackModal({ open, onClose }: Props) {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [widget, setWidget] = useState<iFeedbackWidgetResponse | null>(null);
    const [selected, setSelected] = useState<iFeedbackOption | null>(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        new WhatsNewAPIs()
            .feedbackWidget()
            .then((res) => {
                if (!BaseAPIs.hasError(res)) setWidget(res);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!open) return;
        setDone(false);
        setSelected(null);
        setMessage("");
        setError("");
    }, [open]);

    const themeColor = widget?.settings?.themeColor || "#6500ff";
    const messageRequired = selected?.type === "other";

    const submit = async () => {
        if (!selected) {
            setError("Please pick an option.");
            return;
        }
        if (messageRequired && !message.trim()) {
            setError("Please tell us a little more.");
            return;
        }
        setSubmitting(true);
        setError("");
        const res = await new WhatsNewAPIs().submitFeedback({
            type: selected.type,
            option_name: selected.name,
            emoji: selected.emoji,
            message: message.trim() || undefined,
            from_url: window.location.href,
        });
        setSubmitting(false);
        if (BaseAPIs.hasError(res)) {
            setError("Could not send feedback. Please try again.");
            return;
        }
        setDone(true);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            style={{ zIndex: 100200 }}
            PaperProps={{ style: { borderRadius: 12 } }}
        >
            <DialogContent style={{ padding: "24px 28px 28px" }}>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    style={{ position: "absolute", right: 12, top: 12, color: "#5f6368" }}
                >
                    <CloseIcon />
                </IconButton>

                {loading && (
                    <Box display="flex" justifyContent="center" style={{ padding: 32 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && done && (
                    <Typography style={{ padding: "32px 0", textAlign: "center" }}>Thanks for your feedback!</Typography>
                )}

                {!loading && !done && (
                    <>
                        <Typography
                            style={{ fontSize: 22, fontWeight: 600, paddingRight: 40, marginBottom: 24, color: "#202124" }}
                        >
                            {widget?.settings?.question || "How has your experience been?"}
                        </Typography>

                        <Box style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                            {(widget?.options || []).map((opt) => {
                                const isSelected = selected?.name === opt.name;
                                return (
                                    <Box
                                        key={opt.type + opt.name}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => {
                                            setSelected(opt);
                                            setError("");
                                        }}
                                        onKeyDown={(event: React.KeyboardEvent) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                setSelected(opt);
                                                setError("");
                                            }
                                        }}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{
                                            minHeight: 104,
                                            padding: "14px 8px",
                                            cursor: "pointer",
                                            borderRadius: 10,
                                            border: `1px solid ${tint(themeColor, isSelected ? "" : "40")}`,
                                            background: tint(themeColor, isSelected ? "1f" : "0a"),
                                            boxShadow: isSelected ? `0 6px 18px ${tint(themeColor, "26")}` : "none",
                                            transition: "background .15s, box-shadow .15s",
                                        }}
                                    >
                                        <span style={{ fontSize: 28, lineHeight: 1.1 }}>{opt.emoji}</span>
                                        <Typography
                                            style={{ marginTop: 8, fontSize: 13, textAlign: "center", color: "#202124" }}
                                        >
                                            {opt.name}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        {selected && (
                            <Box style={{ marginTop: 20 }}>
                                <TextField
                                    autoFocus
                                    label={messageRequired ? "Tell us more" : "Tell us more (optional)"}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    style={{ marginBottom: 16 }}
                                />
                                {error && (
                                    <Typography color="error" style={{ marginBottom: 8, fontSize: 13 }}>
                                        {error}
                                    </Typography>
                                )}
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disabled={submitting}
                                    onClick={submit}
                                    style={{ background: themeColor, color: "#fff", textTransform: "none" }}
                                >
                                    {submitting ? "Sending…" : "Send feedback"}
                                </Button>
                            </Box>
                        )}

                        {!selected && error && (
                            <Typography color="error" style={{ marginTop: 16, fontSize: 13 }}>
                                {error}
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
